import dayjs, { Dayjs } from "dayjs";
import {
  IAccountMetadata,
  IPortfolioMetadata,
} from "@pulse-ui/codegen/generated_ts/org_metadata";
import {
  createApiClient,
  grab_base_mktdata_url,
  grab_base_url,
} from "./api_methods";
import { AxiosError, AxiosRequestConfig } from "axios";
import { IPulseApiResponse } from "@pulse-ui/types/formatters";
import { PreferencesState } from "@pulse-ui/types/components/ToolPanel";
import { IManualAdjustmentDto } from "@pulse-ui/codegen/generated_ts/org_entity";
import { IVenueApiKey } from "@pulse-ui/codegen/generated_ts/authn";
import { IStaticOverrideSchedule } from "@pulse-ui/types/components/AGTable";

const PORTFOLIO = "portfolio";
const ACCOUNT = "account";
const ORDER_EVENT = "order_event";
const POSITION_UPDATE = "position_update";
const BALANCE_UPDATE = "balance_update";
const ASSET_MARGIN_REPORT = "asset_margin_report";
const POSITION_MARGIN_REPORT = "position_margin_report";
const TRADE_EVENT = "trade_event";
const TRANSFER_EVENT = "transfer_event";

export const getRestRejectObject = (
  error: AxiosError
): { status: number; data: any; error: string | null } | null => {
  if (error && error.response && error.response.data) {
    let pulseApiResponseObject: IPulseApiResponse = error.response
      .data as IPulseApiResponse;

    return {
      status: error.response.status,
      data: null,
      error: pulseApiResponseObject.error,
    };
  } else {
    return null;
  }
};

export const getOrgs = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(`${grab_base_url(location_origin, "https")}/api/account/v1/orgs`)
        .then((res) => {
          resolve({
            status: 200,
            data: res.data.data[0],
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          if (res.status && res.status === 401) {
            reject({
              status: 401,
              data: null,
              error: "authentication failed.",
            });
          } else {
            reject(getRestRejectObject(res));
          }
        });
    } catch (res: any) {
      if (res.status === 401) {
        reject({
          status: res.status,
          data: null,
          error: "authentication failed.",
        });
      } else {
        reject({
          status: res.status,
          data: null,
          error: res.error,
        });
      }
    }
  });
};

export const getOrgMetadata = (
  org_id: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/metadata`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getOrgUsers = (
  org_id: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/users`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getUser = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(`${grab_base_url(location_origin, "https")}/api/user/v1`)
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const setOrgMetadata = (
  org_metadata: any,
  update_org_metadata: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      update_org_metadata(org_metadata)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        data: null,
        error: error.error,
      });
    }
  });
};

export const getPortfolioId = async (
  id_type: typeof PORTFOLIO | typeof ACCOUNT,
  id: string | null,
  portfolios: IPortfolioMetadata[]
): Promise<{
  status: number;
  data: { portfolio_id: string | null } | null;
  error: any;
}> => {
  try {
    let portfolioId: string | null = null;

    await Promise.all(
      portfolios.map(async (portfolio) => {
        if (id_type === "portfolio" && portfolio.portfolio_id === id) {
          portfolioId = portfolio.portfolio_id;
        } else if (id_type === "account") {
          const accountExists =
            portfolio.accounts &&
            portfolio.accounts.some(
              (account: IAccountMetadata) => account.account_id === id
            );
          if (accountExists) {
            portfolioId = portfolio.portfolio_id;
          }
        }
      })
    );

    return {
      status: 200,
      data: { portfolio_id: portfolioId },
      error: null,
    };
  } catch (error: any) {
    return {
      status: 500,
      data: null,
      error: "Error encountered while trying to get Portfolio IDs",
    };
  }
};

export const setPortfolioById = (
  id: string | null,
  set_portfolio_by_id: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_portfolio_by_id(id)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        data: null,
        error: error.error,
      });
    }
  });
};

export const setAccountById = (
  id: string | null,
  set_account_by_id: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_account_by_id(id)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        data: null,
        error: error.error,
      });
    }
  });
};

export const setSymbolInContext = (
  symbol: string | null,
  set_symbol: (symbol: string | null) => Promise<{ status: number; error: any }>
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_symbol(symbol)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        data: null,
        error: error.error,
      });
    }
  });
};

export const setQuoteAssetInContext = (
  quote_asset: string | null,
  set_quote_asset: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_quote_asset(quote_asset)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        data: null,
        error: error.error,
      });
    }
  });
};

export const setQuoteAssetPreferenceInContext = (
  quote_asset: string | null,
  set_user_preferences: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_user_preferences("quote_asset", quote_asset)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        error: error.error,
      });
    }
  });
};

export const setIntervalPreferenceInContext = (
  interval: string | null,
  set_user_preferences: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_user_preferences("interval", interval)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        error: error.error,
      });
    }
  });
};

export const setStartTimePreferenceInContext = (
  start_time: string | null,
  set_user_preferences: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_user_preferences("start_time", start_time)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        error: error.error,
      });
    }
  });
};

export const setPreferenceContext = (
  preferences: PreferencesState,
  set_user_preferences: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_user_preferences("all", preferences)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        error: error.error,
      });
    }
  });
};

export const setViewInContext = (
  view: string | null,
  set_view: any
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      set_view(view)
        .then((res: any) => resolve(res))
        .catch((error: any) => reject(error));
    } catch (error: any) {
      reject({
        status: error.status,
        data: null,
        error: error.error,
      });
    }
  });
};

export const getBlotterDropcopyData = (
  feed_type:
    | typeof ORDER_EVENT
    | typeof POSITION_UPDATE
    | typeof BALANCE_UPDATE
    | typeof ASSET_MARGIN_REPORT
    | typeof POSITION_MARGIN_REPORT
    | typeof TRADE_EVENT
    | typeof TRANSFER_EVENT,
  account_id: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/dropcopy/v1/history/${account_id}/${feed_type}?limit=500&direction=desc`
        )
        .then((res) => {
          if (res.data.data.length < 1) {
            resolve({
              status: 204,
              data: res.data,
              error: null,
            });
          } else {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getBlotterDropcopyDataByStartDate = (
  feed_type:
    | typeof ORDER_EVENT
    | typeof POSITION_UPDATE
    | typeof BALANCE_UPDATE
    | typeof ASSET_MARGIN_REPORT
    | typeof POSITION_MARGIN_REPORT
    | typeof TRADE_EVENT
    | typeof TRANSFER_EVENT,
  account_id: string,
  location_origin: string,
  start_date: string | null
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      if (start_date) {
        createApiClient(10000)
          .get(
            `${grab_base_url(
              location_origin,
              "https"
            )}/api/dropcopy/v1/history/${account_id}/${feed_type}?limit=25&direction=desc&start_ms=${start_date}`
          )
          .then((res) => {
            if (res.data.data.length < 1) {
              resolve({
                status: 204,
                data: res.data,
                error: null,
              });
            } else {
              resolve({
                status: 200,
                data: res.data,
                error: null,
              });
            }
          })
          .catch((res: AxiosError) => {
            reject(getRestRejectObject(res));
          });
      } else {
        reject({
          status: 500,
          data: null,
          error: "Error while trying to get Blotter rows. Missing Start Date",
        });
      }
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getBlotterDropcopyDataByPaginationToken = (
  feed_type:
    | typeof ORDER_EVENT
    | typeof POSITION_UPDATE
    | typeof BALANCE_UPDATE
    | typeof ASSET_MARGIN_REPORT
    | typeof POSITION_MARGIN_REPORT
    | typeof TRADE_EVENT
    | typeof TRANSFER_EVENT
    | string,
  account_id: string,
  location_origin: string,
  pagination_token: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/dropcopy/v1/history/${account_id}/${feed_type}?token=${pagination_token}`
        )
        .then((res) => {
          if (res.data.data.length < 1) {
            resolve({
              status: 204,
              data: res.data,
              error: null,
            });
          } else {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getBoundDates = (
  account_id: string | null,
  latest_row_timestamp: string,
  location_origin: string,
  feed_type?: string
): Promise<{
  status: number;
  data: {
    min: Dayjs;
    max: Dayjs;
  };
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      if (account_id && latest_row_timestamp) {
        createApiClient(10000)
          .get(
            `${grab_base_url(
              location_origin,
              "https"
            )}/api/dropcopy/v1/history/${account_id}/${
              feed_type || "order_event"
            }?metadata=true`
          )
          .then((res) => {
            if (res.data) {
              resolve({
                status: 200,
                data: {
                  min: dayjs.utc(res.data.first_ts),
                  max: dayjs.utc(latest_row_timestamp),
                },
                error: null,
              });
            } else {
              reject({
                status: 500,
                data: null,
                error:
                  "Temporary: Minimum bound date not set. Data may not be present for a start date greater than 2 weeks ago.",
              });
            }
          })
          .catch((res: AxiosError) => {
            reject(getRestRejectObject(res));
          });
      }
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getPricingAssets = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/portfolio/v1/pricing_assets`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          } else {
            reject({
              status: 500,
              data: null,
              error: "Error getting pricing assets.",
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getRefdataAssets = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(`${grab_base_url(location_origin, "https")}/api/refdata/v1/assets`)
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data.assets,
              error: null,
            });
          } else {
            reject({
              status: 500,
              data: null,
              error: "Error getting refdata assets.",
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getUserRoles = (
  user_id: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(location_origin, "https")}/api/role/v1/${user_id}`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const checkPortfolioPermissions = (
  user_id: string,
  portfolio_id: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/authz/v1/${user_id}/portfolios/${portfolio_id}`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getUserPortfolios = (
  user_id: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/authz/v1/${user_id}/portfolios`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const handleGetPositionSummaryData = (
  portfolio: IPortfolioMetadata,
  quote_asset: string,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise(async (resolve, reject) => {
    try {
      await createApiClient(30000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/portfolio/v1/position_summary?portfolio=${
            portfolio.portfolio_id
          }&quote_asset=${quote_asset}`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: { portfolio_id: portfolio.portfolio_id, ...res.data },
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const handleGetOverviewData = (
  portfolio: IPortfolioMetadata,
  quote_asset: string,
  window: number | null,
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise(async (resolve, reject) => {
    try {
      await createApiClient(30000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/portfolio/v1/overview?portfolio=${
            portfolio.portfolio_id
          }&quote_asset=${quote_asset}${
            window ? `&window=${Math.floor(window)}` : ""
          }`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: { portfolio_id: portfolio.portfolio_id, ...res.data },
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const getUserStates = (
  location_origin: string,
  grid_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/user_grid_state/${grid_id}`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const saveUserState = (
  location_origin: string,
  payload: { grid_id: string; display_name: string; grid_state: string }
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/user_grid_state`,
          payload
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const deleteUserState = (
  location_origin: string,
  payload: { grid_id: string; display_name: string }
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      const config: AxiosRequestConfig = {
        data: payload,
      };

      createApiClient(10000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/user_grid_state`,
          config
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        data: null,
        error: res.error,
      });
    }
  });
};

export const startAlertDefaultConfig = (
  location_origin: string,
  account_id: string
): Promise<{
  status: number;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/alert/v1/check/default/${account_id}/start`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchAlertSchemas = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(location_origin, "https")}/api/alert/v1/check/forms`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchChecks = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/alert/v1/instances/all`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchApiKeys = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(`${grab_base_url(location_origin, "https")}/api/auth/v1/key`)
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchPortfolios = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(location_origin, "https")}/api/alert/v1/portfolios`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchSlackChannels = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/alert/v1/check/destinations/slack`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchTelegramChannels = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/alert/v1/check/destinations/telegram`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchSmsChannels = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/alert/v1/check/destinations/sms`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchGroupDestinations = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/alert/v1/check/group_destinations`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const addUser = (
  location_origin: string,
  org_id: string,
  payload: {
    firstname: string;
    lastname: string;
    email: string;
  }
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/users`,
          payload
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const updateUserRole = (
  location_origin: string,
  org_user_id: string,
  payload: string[]
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .put(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/role/v1/${org_user_id}`,
          payload
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const updateUserAssignedPortfolios = (
  location_origin: string,
  org_user_id: string,
  payload: string[]
): Promise<{ status: number; data: any; error: any }> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .put(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/authz/v1/${org_user_id}/portfolios`,
          { portfolios: payload }
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const deleteUser = (
  location_origin: string,
  org_id: string,
  org_user_id: string
): Promise<{ status: number; data: any; error: any }> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(15000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/users/${org_user_id}`
        )
        .then((res) => {
          if (res.data) {
            resolve({
              status: 200,
              data: res.data,
              error: null,
            });
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const fetchActiveManualAdjustments = (
  location_origin: string,
  account_id: string,
  feed_type: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/adjustments/v1/active?account_id=${account_id}&feed_type=${feed_type}`
        )
        .then((res) => {
          if (res.data) {
            if (res.data.length > 0) {
              resolve({
                status: 200,
                data: res.data,
                error: null,
              });
            } else {
              resolve({
                status: 204,
                data: res.data,
                error:
                  "No active manual adjustments found for this account and feed type.",
              });
            }
          }
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const uploadManualAdjustment = (
  location_origin: string,
  payload: IManualAdjustmentDto[]
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/adjustments/v1/upload`,
          payload
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const deactivateManualAdjustment = (
  location_origin: string,
  adjustment_id: string,
  account_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/adjustments/v1/deactivate?account_id=${account_id}&adjustment_id=${adjustment_id}`
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const savePortfolio = (
  location_origin: string,
  org_id: string,
  payload: { display_name: string }
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/portfolios`,
          payload
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const deletePortfolio = (
  location_origin: string,
  org_id: string,
  portfolio_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/portfolios/${portfolio_id}`
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getAccount = (
  location_origin: string,
  portfolio_id: string,
  account_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/portfolios/${portfolio_id}/venue_accounts/${account_id}`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const saveAccount = (
  location_origin: string,
  portfolio_id: string,
  payload: { display_name: string }
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/portfolios/${portfolio_id}/venue_accounts`,
          payload
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const deleteAccount = (
  location_origin: string,
  portfolio_id: string,
  account_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(15000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/portfolios/${portfolio_id}/venue_accounts/${account_id}`
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          // Not using getRestRejectObject here because the error message is not in the expected format.
          // Sample error response below:
          //   Exception {
          //     message: "request timed out: method: DELETE url: https://account-server.dev.tokyo.pulseprime.systems/api/account/v1/portfolios/27e12012-457e-4fc6-9159-cf0ae9ca383c/venue_accounts/6ab43d23-a801-478b-a11e-ca698389e3a0",
          //     cause: Exception {
          //         message: "request timed out",
          //         cause: Elapsed(
          //             (),
          //         ),
          //     },
          // }
          reject({
            status: res.response?.status,
            data: null,
            error: res.response?.data || "Error deleting account.",
          });
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const updateAccount = (
  location_origin: string,
  portfolio_id: string,
  account_id: string,
  payload: {
    account_id: string;
    display_name: string;
    created: string;
    last_modified: string;
    is_active: boolean;
    strategy: string | null;
  }
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/portfolios/${portfolio_id}/venue_accounts/${account_id}`,
          payload
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getApiKeys = (
  location_origin: string,
  account_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/venue_accounts/${account_id}/apikeys`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const deleteApiKey = (
  location_origin: string,
  account_id: string,
  venue_api_key_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/venue_accounts/${account_id}/apikeys/${venue_api_key_id}`
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const saveApiKey = (
  location_origin: string,
  account_id: string,
  payload: IVenueApiKey
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/venue_accounts/${account_id}/apikeys`,
          payload
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getIpAddresses = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/ips/dropcopy`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getVenueCodes = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/refdata/v1/venue_codes`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getEnabledVenueInstruments = (
  location_origin: string,
  venue: string,
  market_data_enabled: boolean,
  symbol_only: boolean
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/refdata/v1/instruments?venue=${venue}&market_data_enabled=${market_data_enabled}&symbol_only=${symbol_only}`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res?.status || 500,
        error:
          res?.error || `Error fetching venue instruments for venue: ${venue}.`,
      });
    }
  });
};

export const startBookOverrideForSymbol = (
  location_origin: string,
  symbol: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .put(
          `${grab_base_mktdata_url(
            location_origin,
            "https"
          )}/scalesets/mktdata.scaleset`,
          {
            keys: [symbol],
            strategy: "Modulo",
            kvps: { overrides: "book" },
          }
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res?.status || 500,
        error:
          res?.error || `Error starting book override for symbol: ${symbol}.`,
      });
    }
  });
};

export const stopBookOverrideForSymbol = (
  location_origin: string,
  symbol: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      const payload: AxiosRequestConfig = {
        data: {
          keys: [symbol],
          strategy: "Modulo",
          kvps: { overrides: "book" },
        },
      };

      createApiClient(10000)
        .delete(
          `${grab_base_mktdata_url(
            location_origin,
            "https"
          )}/scalesets/mktdata.scaleset`,
          payload
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res?.status || 500,
        error:
          res?.error || `Error stopping book override for symbol: ${symbol}.`,
      });
    }
  });
};

export const getStaticPriceOverrides = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/static_override/v1/active`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const postStaticPriceOverride = (
  location_origin: string,
  payload: IStaticOverrideSchedule[]
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/static_override/v1/upload`,
          payload
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const deleteStaticPriceOverride = (
  location_origin: string,
  symbol: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(15000)
        .delete(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/static_override/v1/deactivate?symbol=${symbol}`
        )
        .then((_res) => {
          resolve({
            status: 200,
            data: null,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getAllInstrumentSymbols = (
  location_origin: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/refdata/v1/instruments?symbol_only=true`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getPortfolios = (
  location_origin: string,
  org_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/portfolios`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getPortfolio = (
  location_origin: string,
  org_id: string,
  portfolio_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/portfolios/${portfolio_id}`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const updatePortfolio = (
  location_origin: string,
  org_id: string,
  portfolio_id: string,
  payload: { display_name: string }
) => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .post(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/orgs/${org_id}/portfolios/${portfolio_id}`,
          payload
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getAccountsByPortfolio = (
  location_origin: string,
  portfolio_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/portfolios/${portfolio_id}/venue_accounts`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};

export const getUserLoginByOrgUserId = (
  location_origin: string,
  org_user_id: string
): Promise<{
  status: number;
  data: any;
  error: any;
}> => {
  return new Promise((resolve, reject) => {
    try {
      createApiClient(10000)
        .get(
          `${grab_base_url(
            location_origin,
            "https"
          )}/api/account/v1/user_login/${org_user_id}`
        )
        .then((res) => {
          resolve({
            status: 200,
            data: res.data,
            error: null,
          });
        })
        .catch((res: AxiosError) => {
          reject(getRestRejectObject(res));
        });
    } catch (res: any) {
      reject({
        status: res.status,
        error: res.error,
      });
    }
  });
};
